import { useCallback } from 'react';

import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { EyePropertyCode } from 'src/models/Eye.model';
import { AddUserRequestBody } from 'src/models/GalenData/AddUserRequestBody.model';
import {
  CustomDeviceDataModel,
  ExamSubmissionData,
} from 'src/models/GalenData/CustomDeviceDataModel.model';
import { DeviceDataSaveRequestBody } from 'src/models/GalenData/DeviceDataSaveRequestBody.model';
import {
  OrderStatusEnum,
  OrderSubmissionData,
} from 'src/models/GalenData/Order.model';
import { QualityPrecheck } from 'src/models/GalenData/QualityPrecheck.model';
import { UserStatusEnum } from 'src/models/GalenData/User.model';
import { UserRoleEnum } from 'src/models/GalenData/UserRole.model';
import { QualityPrecheckEnum } from 'src/models/Grading.model';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { getModels } from 'src/models/Product.model';
import { Session } from 'src/models/Session.model';

import useGetRetinalExamDataModelConfig from 'src/hooks/useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

import useGetCurrentUser from './useGetCurrentUser';

export default function useSubmitSession() {
  const {
    updateSession,
    session,
    updateQualityData,
    practiceSettings: { product, camera },
    cameraData,
    setIsUploadingLeftImage,
    setIsUploadingRightImage,
    setLeftUrl,
    setRightUrl,
  } = useRemoteBoundStore();

  const { practiceId, userId } = useGetCurrentUser();

  const { deviceDataModelId, examSubmissionPropertySetId } =
    useGetRetinalExamDataModelConfig();

  const { tCommon, language } = useUiTranslation();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const createPatient = async ({
    firstName,
    lastName,
    dob,
    id,
  }: {
    firstName: string;
    lastName: string;
    dob: string;
    id: string;
  }): Promise<string> => {
    try {
      const patientUserId = uuidv4();
      const user: AddUserRequestBody = {
        user: {
          userId: patientUserId,
          patientId: id,
          firstName,
          lastName,
          dateOfBirth: dob,
          tenant: { tenantId: import.meta.env.VITE_TENANT_ID },
          roles: [
            {
              defaultRole: true,
              practice: { practiceId: practiceId || '' },
              role: UserRoleEnum.Patient,
            },
          ],
          status: UserStatusEnum.Inactive,
          canViewSensitiveData: false,
          loggableAccount: false,
        },
      };

      await Api.remote.create_user(user);
      return patientUserId;
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      throw error;
    }
  };

  const createExamOrder = async (
    orderId: string,
    patientUserId: string,
    template = session.template,
  ): Promise<string> => {
    try {
      const ownerId = patientUserId;
      await Api.remote._save_device_data_record<OrderSubmissionData>({
        deviceDataId: orderId,
        deviceDataModelId: import.meta.env.VITE_EXAM_ORDER_MODEL_ID,
        devicePropertySetId: import.meta.env
          .VITE_DEVICE_PROPERTY_SET_ID_EXAM_ORDER,
        ownerId,
        data: {
          OrderId: orderId,
          Status: OrderStatusEnum.OPEN,
          OrderStatusDatetime: moment().toISOString(),
          OrderType: template,
        },
      });

      return orderId;
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      throw error;
    }
  };

  const createRemoteSession = useCallback(
    async ({
      OrderId,
      tempSession,
    }: {
      OrderId: string;
      tempSession: Partial<Session>;
    }): Promise<string> => {
      try {
        if (!practiceId) {
          throw new Error(tCommon('message.error.practice-id-is-required'));
        }

        if (!userId) {
          throw new Error(tCommon('message.error.userIdNotFound'));
        }

        const SessionId = uuidv4();
        const ownerId = tempSession.patientUserId;
        const models = getModels(product, camera, tempSession.template);

        const submission: DeviceDataSaveRequestBody<ExamSubmissionData> = {
          deviceDataId: SessionId,
          deviceDataModelId: deviceDataModelId,
          devicePropertySetId: examSubmissionPropertySetId,
          ownerId,
          data: {
            SessionId,
            OrderId,
            ExamSubmissionMetadata: {
              models,
            },
            IsHidden: false,
            ExamSubmissionDatetime: moment().toISOString(),
            ReportDatetime: moment().toISOString(),
            PracticeUserId: userId,
            PracticeId: practiceId,
            Gender: tempSession.gender,
            RightEyeDilatedTime: tempSession.rightEyeDilatedTime,
            LeftEyeDilatedTime: tempSession.leftEyeDilatedTime,
          },
        };

        const response = await Api.remote._save_device_data_record(submission);
        updateSession({
          iid: SessionId,
          orderId: OrderId,
          graded: false,
        });
        return response.data;
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
        throw error;
      }
    },
    [
      practiceId,
      userId,
      product,
      camera,
      deviceDataModelId,
      examSubmissionPropertySetId,
      updateSession,
      tCommon,
      handleHttpRequestError,
      language,
    ],
  );

  const initializeRetinalExam = async (tempSession: Partial<Session>) => {
    try {
      if (!tempSession.patientUserId) {
        throw new Error(tCommon('message.error.patientIdNotFound'));
      }
      const OrderId = uuidv4();
      await Promise.all([
        createRemoteSession({
          OrderId,
          tempSession,
        }),
        createExamOrder(
          OrderId,
          tempSession.patientUserId,
          tempSession.template,
        ),
      ]);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      throw error;
    }
  };

  const submitImage = useCallback(
    async ({
      side,
      imageUrl,
      imageFileName,
      hasQualityPrecheck = false,
      // Optional needed because resubmitExistingSession changes the session.iid before RemoteBoundStore is updated
      deviceDataId = session.iid,
    }: {
      side: EyePropertyCode;
      imageUrl: string;
      imageFileName: string;
      hasQualityPrecheck?: boolean;
      deviceDataId?: string;
    }): Promise<string> => {
      if (side === EyePropertyCode.LEFT) {
        setIsUploadingLeftImage(true);
        setLeftUrl(imageUrl);
      } else {
        setIsUploadingRightImage(true);
        setRightUrl(imageUrl);
      }

      try {
        await Api.remote.upload_media_file({
          deviceDataModelId: deviceDataModelId,
          deviceDataId: deviceDataId,
          propertyCode: `${side}Image`,
          imageUrl,
          imageFileName,
        });

        if (hasQualityPrecheck) {
          const submission: DeviceDataSaveRequestBody<{
            [key: string]: boolean;
          }> = {
            deviceDataId: deviceDataId,
            deviceDataModelId: deviceDataModelId,
            devicePropertySetId: examSubmissionPropertySetId,
            data: { [`${side}ImageUploadComplete`]: true },
          };

          await Api.remote._save_device_data_record(submission);
        }

        return deviceDataId;
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
        throw error;
      } finally {
        if (side === EyePropertyCode.LEFT) {
          setIsUploadingLeftImage(false);
        } else {
          setIsUploadingRightImage(false);
        }
      }
    },
    [
      session.iid,
      deviceDataModelId,
      examSubmissionPropertySetId,
      handleHttpRequestError,
      tCommon,
      language,
      setIsUploadingLeftImage,
      setIsUploadingRightImage,
      setLeftUrl,
      setRightUrl,
    ],
  );

  const fetchQualityStatus = useCallback(async () => {
    try {
      const response = await Api.remote._get_device_data<CustomDeviceDataModel>(
        {
          deviceDataModelId: deviceDataModelId,
          deviceDataId: session.iid,
        },
      );

      return response.data;
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      throw error;
    }
  }, [
    session.iid,
    deviceDataModelId,
    handleHttpRequestError,
    tCommon,
    language,
  ]);

  const pollQualityStatus = useCallback(async () => {
    try {
      const data = await fetchQualityStatus();

      const qualityResult = {
        LeftEyeQuality: data?.data?.LeftEyeQuality?.value,
        RightEyeQuality: data?.data?.RightEyeQuality?.value,
        LeftEyeQualityJson: data?.data?.LeftEyeQualityJson?.value,
        RightEyeQualityJson: data?.data?.RightEyeQualityJson?.value,
      };
      updateQualityData(qualityResult);

      return qualityResult;
    } catch (error) {
      console.error('Error fetching quality status:', error);
      const errorResult = {
        LeftEyeQuality: QualityPrecheckEnum.Reject,
        RightEyeQuality: QualityPrecheckEnum.Reject,
      };
      updateQualityData(errorResult);
      return errorResult;
    }
  }, [fetchQualityStatus, updateQualityData]);

  const setRemoteQualityData = async (
    sessionId: string,
    data: QualityPrecheck,
  ) => {
    try {
      await Api.remote._save_device_data_record({
        deviceDataId: sessionId,
        deviceDataModelId: deviceDataModelId,
        devicePropertySetId: examSubmissionPropertySetId,
        data,
      });
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      throw error;
    }
  };

  const setUploadStatus = async (
    deviceDataId = session.iid,
  ): Promise<string> => {
    try {
      const CameraSn = cameraData?.deviceSN || '';
      const CameraVersion = cameraData?.versionInfo.version || '';

      const res = await Api.remote._save_device_data_record<{
        UploadStatusComplete: boolean;
        CameraSn: string;
        CameraVersion: string;
      }>({
        deviceDataModelId: deviceDataModelId,
        devicePropertySetId: examSubmissionPropertySetId,
        deviceDataId,
        data: {
          UploadStatusComplete: true,
          CameraSn,
          CameraVersion,
        },
      });

      return res.data;
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      throw error;
    }
  };

  const setIsHiddenToTrue = async (deviceDataId: string): Promise<void> => {
    try {
      await Api.remote._save_device_data_record({
        deviceDataId,
        deviceDataModelId: deviceDataModelId,
        devicePropertySetId: examSubmissionPropertySetId,
        data: { IsHidden: true },
      });
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      throw error;
    }
  };

  const createRetakeSession = async (): Promise<string> => {
    try {
      if (!session.orderId) {
        throw new Error(tCommon('message.error.orderIdNotFound'));
      }

      const [, sessionId] = await Promise.all([
        setIsHiddenToTrue(session.iid),
        createRemoteSession({
          OrderId: session.orderId,
          tempSession: session,
        }),
      ]);

      return sessionId;
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      throw error;
    }
  };

  const submitRetakeImage = async ({
    sessionId,
    side,
    imageUrl,
    imageFileName,
    qualityData,
  }: {
    sessionId: string;
    side: EyePropertyCode;
    imageUrl: string;
    imageFileName: string;
    qualityData?: QualityPrecheck;
  }): Promise<void> => {
    try {
      if (side === EyePropertyCode.LEFT) {
        setIsUploadingLeftImage(true);
      } else {
        setIsUploadingRightImage(true);
      }
      await Promise.all([
        Api.remote.upload_media_file({
          deviceDataModelId: deviceDataModelId,
          deviceDataId: sessionId,
          propertyCode: `${side}Image`,
          imageUrl,
          imageFileName,
        }),
        qualityData &&
          setRemoteQualityData(sessionId, {
            [`${side}EyeQuality`]: qualityData[`${side}EyeQuality`],
            [`${side}EyeQualityJson`]: qualityData[`${side}EyeQualityJson`],
          }),
      ]);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      throw error;
    } finally {
      if (side === EyePropertyCode.LEFT) {
        setIsUploadingLeftImage(false);
      } else {
        setIsUploadingRightImage(false);
      }
    }
  };

  return {
    createRemoteSession,
    createPatient,
    createExamOrder,
    initializeRetinalExam,
    submitImage,
    fetchQualityStatus,
    pollQualityStatus,
    setRemoteQualityData,
    setUploadStatus,
    createRetakeSession,
    submitRetakeImage,
  };
}
